
'use client';

import { useState, useEffect, useRef } from 'react';

export default function Portfolio() {
  const [activeFilter, setActiveFilter] = useState('All');
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredProject, setHoveredProject] = useState<number | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  const filters = [
    { name: 'All', icon: 'ri-apps-line' },
    { name: 'Branding', icon: 'ri-palette-line' },
    { name: 'Film', icon: 'ri-film-line' },
    { name: 'Outdoor', icon: 'ri-road-map-line' },
    { name: 'Garments', icon: 'ri-t-shirt-line' }
  ];

  const projects = [
    {
      id: 1,
      title: 'Corporate Brand Identity',
      category: 'Branding',
      client: 'Enterprise Client',
      year: '2024',
      image: 'https://asili.co.ug/visuals/assets/img/image-3.jpg.jpeg',
      span: 'md:col-span-2 md:row-span-2'
    },
    {
      id: 2,
      title: 'Brand Collateral Design',
      category: 'Branding',
      client: 'Corporate Partner',
      year: '2024',
      image: 'https://asili.co.ug/visuals/assets/img/image-4.jpg.jpeg',
      span: 'md:col-span-1'
    },
    {
      id: 3,
      title: 'Visual Identity System',
      category: 'Branding',
      client: 'Business Client',
      year: '2024',
      image: 'https://asili.co.ug/visuals/assets/img/image-5.jpg.jpeg',
      span: 'md:col-span-1'
    },
    {
      id: 4,
      title: 'Brand Communication Materials',
      category: 'Branding',
      client: 'Strategic Partner',
      year: '2024',
      image: 'https://asili.co.ug/visuals/assets/img/image-6.jpg.jpeg',
      span: 'md:col-span-1 md:row-span-2'
    },
    {
      id: 5,
      title: 'Corporate Branding Package',
      category: 'Branding',
      client: 'Enterprise Solutions',
      year: '2024',
      image: 'https://asili.co.ug/visuals/assets/img/image-7.jpg.jpeg',
      span: 'md:col-span-1'
    },
    {
      id: 6,
      title: 'Brand Strategy Implementation',
      category: 'Branding',
      client: 'Market Leader',
      year: '2024',
      image: 'https://asili.co.ug/visuals/assets/img/image-8.jpg.jpeg',
      span: 'md:col-span-1'
    },
    {
      id: 7,
      title: 'Professional Brand Assets',
      category: 'Branding',
      client: 'Industry Partner',
      year: '2024',
      image: 'https://asili.co.ug/visuals/assets/img/image-9.jpg.jpeg',
      span: 'md:col-span-1'
    },
    {
      id: 8,
      title: 'Integrated Brand System',
      category: 'Branding',
      client: 'Corporate Client',
      year: '2024',
      image: 'https://asili.co.ug/visuals/assets/img/image-10.jpg.jpeg',
      span: 'md:col-span-1'
    },
    {
      id: 9,
      title: 'Apparel Brand Design',
      category: 'Garments',
      client: 'Fashion Partner',
      year: '2024',
      image: 'https://asili.co.ug/visuals/assets/img/NEWWWWCLO.jpg.jpeg',
      span: 'md:col-span-2'
    },
    {
      id: 10,
      title: 'Safety Reflector Branding',
      category: 'Outdoor',
      client: 'Safety Solutions',
      year: '2024',
      image: 'https://asili.co.ug/visuals/assets/img/reflectors.jpg.jpeg',
      span: 'md:col-span-1'
    },
    {
      id: 11,
      title: 'Corporate Identity Suite',
      category: 'Branding',
      client: 'Business Enterprise',
      year: '2024',
      image: 'https://asili.co.ug/visuals/assets/img/reall td.jpg.jpeg',
      span: 'md:col-span-1'
    },
    {
      id: 12,
      title: 'Brand Positioning Campaign',
      category: 'Branding',
      client: 'Strategic Client',
      year: '2024',
      image: 'https://asili.co.ug/visuals/assets/img/tdfav.jpg.jpeg',
      span: 'md:col-span-2'
    }
  ];

  const filteredProjects = activeFilter === 'All'
    ? projects
    : projects.filter(p => p.category === activeFilter);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="portfolio" className="relative py-12 sm:py-16 lg:py-20 bg-[#FAFAF9] overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[300px] sm:w-[400px] lg:w-[500px] h-[300px] sm:h-[400px] lg:h-[500px] bg-gradient-to-bl from-[#038C7F]/5 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[200px] sm:w-[250px] lg:w-[300px] h-[200px] sm:h-[250px] lg:h-[300px] bg-gradient-to-tr from-[#F2780C]/5 to-transparent rounded-full blur-3xl"></div>
      </div>

      {/* Section Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-6 sm:mb-8 lg:mb-10">
        <div className={`flex flex-col lg:flex-row items-start lg:items-end justify-between gap-4 sm:gap-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div>
            <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
              <div className="w-8 h-8 sm:w-9 sm:h-9 bg-[#038C7F]/10 rounded-lg flex items-center justify-center">
                <i className="ri-gallery-line text-base sm:text-lg text-[#038C7F]"></i>
              </div>
              <span className="text-[10px] sm:text-xs font-bold text-[#038C7F] uppercase tracking-widest">Portfolio</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#0D0D0D]">
              SELECTED <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#038C7F] to-[#F2780C]">WORK</span>
            </h2>
          </div>

          {/* Filter Tabs - Scrollable on mobile */}
          <div className="w-full lg:w-auto overflow-x-auto pb-2 -mx-4 px-4 lg:mx-0 lg:px-0 scrollbar-hide">
            <div className="flex gap-2 min-w-max lg:min-w-0 lg:flex-wrap">
              {filters.map(filter => (
                <button
                  key={filter.name}
                  onClick={() => setActiveFilter(filter.name)}
                  className={`group flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeFilter === filter.name
                      ? 'bg-[#0D0D0D] text-white shadow-lg'
                      : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  <i className={`${filter.icon} text-xs sm:text-sm ${activeFilter === filter.name ? 'text-[#038C7F]' : 'text-gray-400'}`}></i>
                  {filter.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Portfolio Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 auto-rows-[160px] sm:auto-rows-[180px]">
          {filteredProjects.map((project, index) => (
            <div
              key={project.id}
              className={`${project.span} group relative overflow-hidden rounded-lg sm:rounded-xl cursor-pointer transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 100}ms` }}
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
            >
              <img
                src={project.image}
                alt={project.title}
                className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
              />

              <div className={`absolute inset-0 transition-opacity duration-500 ${hoveredProject === project.id ? 'opacity-100' : 'opacity-0 sm:opacity-0'}`}>
                <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D] via-[#0D0D0D]/80 to-transparent"></div>
              </div>

              {/* Always visible on mobile, hover on desktop */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0D0D0D]/90 via-[#0D0D0D]/40 to-transparent sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300"></div>

              <div className="absolute top-2 sm:top-3 left-2 sm:left-3 z-10">
                <span className="px-2 sm:px-2.5 py-0.5 sm:py-1 bg-white/90 backdrop-blur-sm text-[#0D0D0D] text-[10px] sm:text-xs font-bold rounded-full">
                  {project.category}
                </span>
              </div>

              <div className={`absolute inset-0 flex flex-col justify-end p-3 sm:p-4 lg:p-5 transition-all duration-500 ${hoveredProject === project.id ? 'opacity-100 translate-y-0' : 'opacity-100 sm:opacity-0 translate-y-0 sm:translate-y-4'}`}>
                <div className="flex items-center gap-2 mb-1 sm:mb-2">
                  <span className="text-[10px] sm:text-xs font-medium text-[#038C7F]">{project.client}</span>
                  <span className="w-1 h-1 bg-white/40 rounded-full"></span>
                  <span className="text-[10px] sm:text-xs font-medium text-white/60">{project.year}</span>
                </div>

                <h3 className="text-base sm:text-lg lg:text-xl xl:text-2xl font-bold text-white mb-2 sm:mb-3 leading-tight">
                  {project.title}
                </h3>

                <a
                  href="/portfolio"
                  onClick={(e) => {
                    e.preventDefault();
                    window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE('/portfolio');
                  }}
                  className="inline-flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-white group/link"
                >
                  <span>View Case Study</span>
                  <div className="w-5 h-5 sm:w-7 sm:h-7 bg-[#038C7F] rounded-full flex items-center justify-center group-hover/link:bg-[#F2780C] transition-colors">
                    <i className="ri-arrow-right-up-line text-xs sm:text-base"></i>
                  </div>
                </a>
              </div>

              <div className={`absolute bottom-0 right-0 w-14 sm:w-20 h-14 sm:h-20 transition-all duration-500 hidden sm:block ${hoveredProject === project.id ? 'opacity-100' : 'opacity-0'}`}>
                <div className="absolute bottom-2 sm:bottom-3 right-2 sm:right-3 w-8 sm:w-10 h-8 sm:h-10 border-r-2 border-b-2 border-[#038C7F]"></div>
              </div>
            </div>
          ))}
        </div>

        {/* View All CTA */}
        <div className={`text-center mt-8 sm:mt-10 transition-all duration-700 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <a
            href="/portfolio"
            onClick={(e) => {
              e.preventDefault();
              window.REACT_APP_NAVIGATE && window.REACT_APP_NAVIGATE('/portfolio');
            }}
            className="group inline-flex items-center gap-2 sm:gap-3 px-6 sm:px-8 py-3 sm:py-4 bg-[#0D0D0D] text-white text-xs sm:text-sm font-semibold rounded-full hover:shadow-2xl transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <span>View All Case Studies</span>
            <div className="w-6 h-6 sm:w-8 sm:h-8 bg-[#038C7F] rounded-full flex items-center justify-center group-hover:bg-[#F2780C] transition-colors">
              <i className="ri-arrow-right-line text-sm sm:text-lg group-hover:translate-x-0.5 transition-transform"></i>
            </div>
          </a>
        </div>
      </div>

      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
}
